import React from 'react';
import {
  FILTER_PARAMS,
  ASSET_STUDIO,
  VISUAL_AI_INNER_TABS,
  SENSOR_DATA_INNER_TABS,
} from '@/types/shared/navigation-tabs.enums';
import { HEALTH_STATUS, SENSOR_TYPE, SENSORS_DATA_TYPE } from '@/const';
import { isAfter, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { setVisualAIActiveItemIndex } from '@/store/slices/assetStudioSlice';
import { useParams } from 'react-router-dom';
import { useQueryNavigation } from '@/hooks';
import { RootState } from '@/store/store';

interface navigateEvidenceProps {
  id: string | null;
  sourceType: string;
  diagnosticDate: string;
  healthStatus?: string;
  assetId?: string;
  componentName?: string;
}

export const useNavigateEvidence = ({
  id,
  sourceType,
  diagnosticDate,
  healthStatus,
  assetId,
  componentName,
}: navigateEvidenceProps) => {
  const { selectedDiagnostic } = useSelector((state: RootState) => state.assetsStudio);
  const { navigateWithQuery, pathname, currentParams } = useQueryNavigation();
  const { id: urlAssetId, siteId } = useParams();

  const disaptch = useDispatch();

  //video evidence are avaliable only from 4th of Apr 2024 onwards
  const videoSupportedFrom = parseISO('2024-04-08');
  const isCamera = sourceType?.toLowerCase() === SENSOR_TYPE.Camera;
  const isHealthy = healthStatus?.toLowerCase() === HEALTH_STATUS.HEALTHY;
  const isVideoSupported = isAfter(parseISO(diagnosticDate), videoSupportedFrom);
  const isAssetStudio = currentParams[FILTER_PARAMS.ASSET_STUDIO] && urlAssetId;
  const isBeltComponent = componentName?.toLowerCase()?.includes('belt');
  const isOilFault = sourceType?.toLowerCase()?.includes('oil');

  // Check the selected diagnostic - get all diagnostics for the component on the selected date
  const selectedDiagnosticData = selectedDiagnostic?.[1]; // [date, diagnosticData]
  const componentDiagnostics =
    selectedDiagnosticData?.assets?.[0]?.components?.find((comp: any) => comp.component_id === id)?.diagnostics || [];

  // Get all fault types from the selected diagnostic
  const faultTypes = componentDiagnostics.map((d: any) => d.fault_type?.toLowerCase() || '');
  const hasOnlyBeltDefects = faultTypes.length > 0 && faultTypes.every((ft: string) => ft.includes('defect'));
  const hasBeltDefects = faultTypes.some((ft: string) => ft.includes('defect'));
  const hasOtherFaults = faultTypes.some((ft: string) => !ft.includes('defect'));

  // Debug logging
  //@TODO: make it generic
  const targetPath = isAssetStudio
    ? assetId !== urlAssetId
      ? `/sites/${siteId}/machines/${assetId}/components`
      : pathname
    : `${assetId}/components`;
  const linkText =
    isVideoSupported && isCamera
      ? 'Visual AI'
      : (isCamera && !isVideoSupported) || (!isCamera && !isHealthy)
      ? 'View'
      : isOilFault
      ? 'Oil Report'
      : isHealthy && 'Sensor Data';

  const navigateEvidence = () => {
    // Determine which tab to navigate to based on diagnostics
    // If only belt defects → Belt Defects tab
    // If belt component with other faults (or belt defects + other) → Belt Misalignment tab
    // Otherwise → no specific tab
    const visualAITab = hasOnlyBeltDefects
      ? VISUAL_AI_INNER_TABS.BELT_DEFECTS
      : isBeltComponent && (hasOtherFaults || hasBeltDefects)
      ? VISUAL_AI_INNER_TABS.BELT_MISALIGNMENT
      : isBeltComponent
      ? VISUAL_AI_INNER_TABS.BELT_MISALIGNMENT
      : null;


    // Prepare params object - always include tab to clear or set it
    const navigationParams: any = {
      [FILTER_PARAMS.SELECTED_COMPONENT]: id,
      [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.VISUAL_AI,
    };

    // Only add tab if we have a specific one, otherwise don't include it to use default
    if (visualAITab) {
      navigationParams[FILTER_PARAMS.TAB] = visualAITab;
    }

    if (isOilFault) {
      navigateWithQuery(pathname, {
        replaceParams: {
          [FILTER_PARAMS.SELECTED_COMPONENT]: id,
          [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
        },
        addParams: {
          tab: SENSOR_DATA_INNER_TABS.OIL_REPORT,
        },
      });
    }

    if (!isAssetStudio) {
      disaptch(setVisualAIActiveItemIndex(0)) &&
        navigateWithQuery(targetPath, {
          replaceParams: navigationParams,
        });
    }
    if (isCamera) {
      isVideoSupported &&
        disaptch(setVisualAIActiveItemIndex(0)) &&
        navigateWithQuery(targetPath, {
          replaceParams: {
            ...navigationParams,
            [FILTER_PARAMS.SELECTED_DIAGNOSTIC]: null, // Clear stale param — Visual AI reads date from Redux
          },
        });
    } else {
      if (isHealthy) {
        navigateWithQuery(pathname, {
          replaceParams: {
            [FILTER_PARAMS.SELECTED_COMPONENT]: id,
            [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.SENSOR_DATA,
          },
          addParams: {
            group: SENSORS_DATA_TYPE.PI,
          },
        });
      }
    }
  };

  if (!(id && sourceType && diagnosticDate)) {
    return {
      navigateEvidence: () => {},
      isCamera: false,
      isVideoSupported: false,
      isHealthy: false,
      linkText: '',
    };
  }

  return { navigateEvidence, isCamera, isVideoSupported, isHealthy, linkText };
};
